import { useQuery } from '@tanstack/react-query';
import { chunk, maxBy } from 'lodash';

import { DeviceLatestData } from '@/api/devices';
import { SPOTTER_SDK_TOKEN } from '@/api/oceansApi/constants';
import { RequestOptions, axios } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';
import { APIResponse, LocationData } from '@/common/types';

type FleetSpotterItem = {
    spotter_id: string;
    spotter_nick: string | null;
    owner: string;
    org: string;
    data: {
        lat: number;
        lon: number;
        battery_voltage: number;
        battery_power: number;
        solar_voltage: number;
        barometer: number | null;
        humidity: number;
        temp: number;
        sea_surface_temp: number | null;
        transmit_time: string;
        message_id: number;
        valid: boolean;
    };
    valid: boolean;
};

type GetFleetSpottersLatestDataRequest = {
    useCache: boolean;
    spotters: string;
};

export type FleetDeviceLatestData = DeviceLatestData & { owner: string; org: string };

export const getFleetSpottersLatestData = async (
    params: GetFleetSpottersLatestDataRequest,
    options?: RequestOptions
): Promise<APIResponse<FleetDeviceLatestData[]>> => {
    const rawData = await axios.get<unknown, { spotters: FleetSpotterItem[] }>('/api/fleet/spotters/latest-data', {
        params,
        headers: { token: SPOTTER_SDK_TOKEN },
        ...options,
    });

    const data = rawData.spotters.map(({ spotter_id, spotter_nick, owner, org, data }) => {
        const location: LocationData = {
            latitude: data.lat,
            longitude: data.lon,
            timestamp: data.transmit_time,
        };
        return {
            spotterId: spotter_id,
            name: spotter_nick,
            owner,
            org,
            track: [location],
            timeLastUpdatedUTC: data.transmit_time,
            ...(data.sea_surface_temp != null && {
                surfaceTemperature: { degrees: data.sea_surface_temp, location },
            }),
            ...(data.barometer != null && {
                airPressure: { pressure: data.barometer, location },
            }),
        };
    });

    return { data, status: 'ok' };
};

type UseFleetSpottersLatestDataOptions = {
    spotters: string[];
    useCache: boolean;
    config?: QueryConfig<typeof getFleetSpottersLatestData>;
};

/**
 * How many spotters one request asks about.
 *
 * This bounds the query the endpoint runs, not how many requests are in flight: the axios client caps
 * concurrency for the whole app, and these batches take their turn in that queue like anything else.
 */
const BATCH_SIZE = 25;

export const useFleetSpottersLatestData = ({ spotters, useCache, config }: UseFleetSpottersLatestDataOptions) =>
    useQuery({
        queryKey: ['fleet-spotters-latest-data', spotters.toSorted().join(',')],
        queryFn: async ({ signal }) => {
            const batches = await Promise.all(
                chunk(spotters, BATCH_SIZE).map((batch) =>
                    getFleetSpottersLatestData({ spotters: batch.join(','), useCache }, { signal })
                )
            );
            // in the order the batches were cut, so the result follows the order the spotters were asked for
            const results: FleetDeviceLatestData[] = batches.flatMap((response) => response.data);
            const mostRecent = maxBy(results, (d) =>
                d.timeLastUpdatedUTC ? new Date(d.timeLastUpdatedUTC).getTime() : 0
            );
            const cutoff = mostRecent?.timeLastUpdatedUTC
                ? new Date(mostRecent.timeLastUpdatedUTC).getTime() - 24 * 60 * 60 * 1000
                : null;
            const data = cutoff
                ? results.filter((d) => d.timeLastUpdatedUTC && new Date(d.timeLastUpdatedUTC).getTime() >= cutoff)
                : results;
            return { data, status: 'ok' };
        },
        staleTime: 1000 * 60 * 5,
        ...config,
    });
